import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ShoppingCart, Search, Loader2 } from 'lucide-react';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get('/admin/orders', { params: { limit: 100 } });
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const filtered = orders.filter(o =>
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
    o.product_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.creator_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.ref?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="admin-orders">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Platform Orders</h1>
        <p className="text-sm text-slate-500 mt-1">{total} total order{total !== 1 ? 's' : ''} across all creators</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9 bg-white border-slate-200" placeholder="Search by customer, product, creator, or ref..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ref</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Creator</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(o => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{o.ref}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{o.customer_name}</p>
                    <p className="text-xs text-slate-400">{o.customer_email}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{o.product_name}</p>
                    <p className="text-xs text-slate-400">{o.product_type}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{o.creator_name}</p>
                    <p className="text-xs text-slate-400">{o.creator_email}</p>
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">RM {(o.amount || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant={o.status === 'paid' ? 'default' : o.status === 'cancelled' ? 'destructive' : 'secondary'}>
                    {o.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-400">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' }) : '-'}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-400">
                  {search ? 'No orders match your search' : 'No orders yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
